// src/rota/index.js
// Rota — extracted from index.html (v105.x sprint, auto-generated header).
// Bundled by esbuild into dist/rota.js. The HTML loads it via
//   <script src="dist/rota.js"></script>
// and call sites use `Rota.<sub>.<fn>()` (e.g.
//   Rota.state.shiftKey()
//   Rota.render.renderRotaWeek()
// ).
//
// Backwards compat: index.html's trailing script block re-exposes the
// moved functions on window so existing call sites — both inline JS and
// onclick="" attributes — keep working without HTML edits.
//
// Free variables (DB, currentUser, currentStore, escapeHtml, WORKER_URL,
// fetch, console, document, window, ...) live in the inline globals
// scope of index.html. esbuild leaves them as bare references at runtime.

// ── state ─────────────────────────────────────────────
export const state = {};

function shiftKey(staffId,day) { return staffId+'_'+day; }
state.shiftKey = shiftKey;

function getShift(staffId,day,weekStart) {
  if (weekStart) {
    var wk = weekStart instanceof Date ? localDateStr(weekStart) : weekStart;
    var wkKey = wk+'_'+staffId+'_'+day;
    if (shifts[wkKey] !== undefined) return shifts[wkKey];
  }
  return shifts[shiftKey(staffId,day)];
}
state.getShift = getShift;

// v129.156 (split shifts) — the SECOND leg of a day, if any. Mirrors getShift
// against the parallel shifts2 map. Callers treat undefined as "no split".
function getShift2(staffId,day,weekStart) {
  if (weekStart) {
    var wk = weekStart instanceof Date ? localDateStr(weekStart) : weekStart;
    var wkKey = wk+'_'+staffId+'_'+day;
    if (shifts2[wkKey] !== undefined) return shifts2[wkKey];
  }
  return shifts2[shiftKey(staffId,day)];
}
state.getShift2 = getShift2;

// v136.8 (boot audit): the week's targets are made sure of next to its shifts (a store
// device reads its own store's targets for a window; a week beyond it is read here, so
// the redraw every caller does after this already has them). Never rejects.
function targetsForWeek(weekStart) {
  try {
    if (typeof TARGETS === 'undefined' || typeof TARGETS.ensureWeek !== 'function') return Promise.resolve();
    var st = (typeof currentStore !== 'undefined' && currentStore) ? currentStore : null;
    return Promise.resolve(TARGETS.ensureWeek(st, weekStart)).catch(function(){});
  } catch (e) { return Promise.resolve(); }
}

async function loadShiftsFromDB(weekStart) {
  if (typeof DB === 'undefined') return;
  var tgtP = targetsForWeek(weekStart);   // v136.8: alongside the shifts read, awaited in the finally below
  try {
    var wk = weekStart instanceof Date ? localDateStr(weekStart) : weekStart;
    // v131.38: tokenless reads name the store (the server returns home + cover-in rows for it).
    var rows = await DB.shifts.list({ week: wk, store: (typeof currentStore !== 'undefined' && currentStore) ? currentStore : undefined });
    if (!Array.isArray(rows)) return;
    rows.forEach(function(row){
      // Key format: YYYY-MM-DD_staffId_DayAbbr
      var sk = row.week_start + '_' + row.staff_id + '_' + row.day;
      // v129.156 (split shifts, #1177 step 4) — a second leg (seq >= 2) lives
      // in the PARALLEL shifts2 map, so the one-slot-per-day model every
      // existing reader relies on stays byte-identical. shifts2 is declared in
      // index.html next to shifts (cross-bundle state stays in the HTML — the
      // frontend-breakup rule).
      if ((row.seq || 1) >= 2) {
        shifts2[sk] = {
          type: row.type,
          start: row.start_time || undefined,
          finish: row.end_time || undefined,
          lunch: row.lunch_mins || 0,
          lunchAt: row.lunch_at || undefined,
          notes: row.notes || '',
          workStore: row.work_store || null,
          leaveReason: row.leave_reason || null,
          varianceToil: row.variance_toil || null,
          // v132.48: hours for a TOIL day (null = a whole day, still 8)
          toilHours: (row.toil_hours == null ? null : Number(row.toil_hours)),
          seq: row.seq,
        };
        return;
      }
      shifts[sk] = {
        type: row.type,
        start: row.start_time || undefined,
        finish: row.end_time || undefined,
        lunch: row.lunch_mins || 0,
        lunchAt: row.lunch_at || undefined,
        notes: row.notes || '',
        // v48: where the person is physically working this shift. null/undefined
        // means home store. If set to a different store, it's a cover shift.
        workStore: row.work_store || null,
        // v129.15: APL reason (maternity/paternity/compassionate/other)
        leaveReason: row.leave_reason || null,
        // v129.68: manager-approved TOIL variance (compute-on-read; the modal
        // pre-selects TOIL when reopening a flagged shift)
        varianceToil: row.variance_toil || null,
        // v132.48: hours for a TOIL day (null = a whole day, still 8)
        toilHours: (row.toil_hours == null ? null : Number(row.toil_hours)),
      };
    });
    dlog('[DB] Loaded ' + rows.length + ' shifts for week ' + wk);
  } catch(e) {
    console.warn('[DB] Shifts load failed:', e.message);
  } finally {
    await tgtP;
  }
}
state.loadShiftsFromDB = loadShiftsFromDB;

function shiftDateStr(dateStr, days) {
  var d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return localDateStr(d);
}
state.shiftDateStr = shiftDateStr;

function getContractedHours(staffId){
  var s=ALL_STAFF.find(function(x){return x.id===staffId;});
  if(!s)return 48;
  if(s.contractedHours!=null)return s.contractedHours;
  return isEmirati(s.name)?36:48;
}
state.getContractedHours = getContractedHours;

// ── render ─────────────────────────────────────────────
export const render = {};

function renderRotaWeek(){
  var homeStaff=getStoreStaff(currentStore);
  var targets=getTargets(currentStore,currentWeekStart);
  var maxTarget=Math.max.apply(null,Object.values(targets));
  // Week label is set by updateWeekLabel() in renderCurrentRotaView (v70).
  var displayDays=getDisplayDays();
  var wkStr=localDateStr(currentWeekStart);

  // ── v48 CROSS-STORE STAFF DETECTION ────────────────────────────
  // Find anyone whose shift at any day this week has workStore === currentStore
  // but their HOME store is NOT currentStore. These are "covering at us" staff.
  var homeStaffIds = {};
  homeStaff.forEach(function(s){ homeStaffIds[s.id] = true; });
  var crossStoreIds = {};
  Object.keys(shifts).forEach(function(key){
    // Only this week's shift keys (format: YYYY-MM-DD_staffId_Day)
    if (key.indexOf(wkStr + '_') !== 0) return;
    var sh = shifts[key];
    if (!sh || !sh.workStore) return;
    if (sh.workStore !== currentStore) return;
    // Parse staff_id from the key
    var parts = key.split('_');
    if (parts.length < 3) return;
    var sid = parseInt(parts[3] || parts[parts.length - 2]); // key is YYYY-MM-DD_sid_day so split by _ gives [YYYY,MM,DD,sid,day]
    if (isNaN(sid)) return;
    if (!homeStaffIds[sid]) crossStoreIds[sid] = true;
  });
  var crossStoreStaff = Object.keys(crossStoreIds)
    .map(function(id){ return ALL_STAFF.find(function(x){ return x.id === parseInt(id); }); })
    .filter(Boolean);

  // Complete roster = home + cross-store
  var allStaff = homeStaff.concat(crossStoreStaff);
  // Disambiguate first names across full week roster (home + covering staff)
  var displayNames = computeDisplayNames(allStaff);

  // Pre-calculate working heads per day for SPWH metric (anyone physically here)
  var headsPerDay={Mon:0,Tue:0,Wed:0,Thu:0,Fri:0,Sat:0,Sun:0};
  allStaff.forEach(function(s){
    displayDays.forEach(function(day){
      var sh=getShift(s.id,day,currentWeekStart);
      if (!sh) { if (homeStaffIds[s.id]) headsPerDay[day]++; return; } // no shift = default to home
      // v129.18: 'apl' added — it was the first type ever to reach this
      // fall-through, so a person on leave counted as a working HEAD in the
      // header's sales-per-worked-hour, disagreeing with the Heads row below
      // (whose APL branch correctly does not count them).
      if (sh.type !== 'work' && sh.type !== 'off' && sh.type !== 'toil' && sh.type !== 'holiday' && sh.type !== 'sick' && sh.type !== 'oos' && sh.type !== 'apl') {
        headsPerDay[day]++; return;
      }
      if (sh.type === 'work') {
        // Only count if they're actually AT this store
        var atThisStore = !sh.workStore || sh.workStore === currentStore;
        if (atThisStore) headsPerDay[day]++;
      }
    });
  });

  var headHtml='<tr><th style="text-align:start;min-width:90px">Staff</th>';
  displayDays.forEach(function(day,i){
    var dateStr=weekDateStr(currentWeekStart,i);
    var d=new Date(currentWeekStart);d.setDate(d.getDate()+i);
    var dateLabel=d.getDate()+'/'+pad(d.getMonth()+1);
    var holiday=getHolidayForDate(currentStore,dateStr);
    var target=targets[day]||0;
    var pctVal=maxTarget>0?Math.round((target/maxTarget)*100):0;
    var barColor=pctVal>=80?'var(--danger)':pctVal>=60?'#f59e0b':'var(--brand,#009E3D)';
    var thClass=holiday?'class="th-holiday"':'';
    // Sales per worked hour: target / (heads * 8h)
    var heads=headsPerDay[day]||1;
    var spwh=Math.round(target/(heads*8));
    var spwhColor=spwh>400?'var(--danger)':spwh>250?'#f59e0b':'var(--sub)';
    headHtml+='<th '+thClass+'>'+day+'<br><span class="th-date">'+dateLabel+'</span>'
      +'<span class="th-target" style="color:'+barColor+'">'+(target/1000).toFixed(1)+'k</span>'
      +'<span style="font-size:8px;color:'+spwhColor+';display:block;font-weight:700">'+spwh+'/h</span>'
      +(holiday?'<span class="th-holiday-name">\uD83C\uDFD6 '+holiday.name.split(' ').slice(0,3).join(' ')+'</span>':'')
      +'</th>';
  });
  headHtml+='<th style="min-width:58px;font-size:9px">Hours</th></tr>';
  document.getElementById('rotaThead').innerHTML=headHtml;
  var bodyHtml='';
  // Reset for body rendering
  headsPerDay={Mon:0,Tue:0,Wed:0,Thu:0,Fri:0,Sat:0,Sun:0};

  // ── Helper to render one staff row (home or cross-store) ──
  function renderStaffRow(s, isCross) {
    var color=s.color||COLORS[s.id%COLORS.length];
    var totalHours=0;     // work hours only — what's actually scheduled
    var nonWorkHours=0;   // holiday + sick + TOIL — paid time off this week
    var coverBadge = isCross
      ? ' <span class="cover-badge">COVER</span><br><span style="font-size:9px;color:var(--sub)">'+s.store+'</span>'
      : '';
    bodyHtml+='<tr'+(isCross?' class="cover-row"':'')+'><td><div class="staff-name-cell"><div class="sn-dot" style="background:'+color+'"></div>'+'<span translate="no">'+escapeHtml(displayNames[s.id]||s.name.split(' ')[0])+'</span>'+coverBadge+'</div></td>';
    displayDays.forEach(function(day,i){
      var sh=getShift(s.id,day,currentWeekStart);
      var dateStr=weekDateStr(currentWeekStart,i);
      var holiday=getHolidayForDate(currentStore,dateStr);
      var holidayBg=holiday?'style="background:var(--rota-warn-soft)"':'';
      var onclick='onclick="requireManager(\'editShift\','+s.id+',\''+day+'\')" style="cursor:pointer"';

      // Determine "is this shift at THIS store?"
      var shWorkStore = sh ? sh.workStore : null;
      var atThisStore = !shWorkStore || shWorkStore === currentStore;

      // Cross-store staff with no shift or a shift at ANOTHER store → show "at home" placeholder
      if (isCross && (!sh || !atThisStore)) {
        bodyHtml += '<td '+holidayBg+' style="opacity:.4;cursor:default"><span style="font-size:10px;color:var(--sub)">\u2014</span></td>';
        return;
      }

      // Home staff with a work shift at a DIFFERENT store → show "→ StoreName" OOS chip
      if (!isCross && sh && sh.type === 'work' && shWorkStore && shWorkStore !== currentStore) {
        var labelShort = shWorkStore.replace('ELC - ','').split(' - ').pop().split(' ').slice(0,2).join(' ');
        bodyHtml += '<td style="background:var(--info-bg);cursor:pointer" '+onclick+'><span class="shift-pill sp-oos">\u2197 '+labelShort+'</span></td>';
        return;
      }

      // Normal shift rendering (at this store)
      if(!sh||sh.type==='work'){
        if(sh&&sh.start){
          headsPerDay[day]++;
          // v127.34 (Arabian Ranches 2 bug report, 26 Jul): the weekly Rota
          // total used to CAP each day at 8h — but since approved variance
          // shifts keep their real times (the Wafi City fix), the excess hours
          // of a long day vanished from the total while the compensating
          // short days counted in full, so "hours used vs hours deducted"
          // could never reconcile. Uncapped now = the same per-day figure the
          // payroll CSV pays (paidHoursForShift). Max(0) guards a shift
          // shorter than its lunch. getWeekHours' 8h cap is DELIBERATE and
          // stays — that one feeds the 48h contract warning, not this display.
          var hrs=Math.max(0,shiftMins(sh.start,sh.finish)/60-(sh.lunch||60)/60);
          totalHours+=hrs;
          // v129.156 (split shifts) \u2014 the day's SECOND leg, rendered as its own
          // chip with its own click (stopPropagation so the cell still opens
          // leg 1). A leg at another store names it. Its hours count in the
          // Rota total (a person's day is the sum of their legs) but the head
          // count stays 1 \u2014 a split day is one person, not two.
          var sh2=getShift2(s.id,day,currentWeekStart);
          var leg2Html='';
          if(sh2&&sh2.type==='work'&&sh2.start){
            totalHours+=Math.max(0,shiftMins(sh2.start,sh2.finish)/60-(sh2.lunch||60)/60);
            var leg2Store=(sh2.workStore&&sh2.workStore!==currentStore)?' \u2197 '+escapeHtml(sh2.workStore.split(' - ').pop().split(' ').slice(0,2).join(' ')):'';
            leg2Html='<div class="shift-pill sp-work" style="margin-top:2px;opacity:.85;font-size:10px" '+
              'onclick="event.stopPropagation();requireManager(\'editShift2\','+s.id+',\''+day+'\')" '+
              'title="Second shift on this day \u2014 click to edit">\uD83D\uDD00 '+sh2.start+'\u2013'+sh2.finish+leg2Store+'</div>';
          }
          bodyHtml+='<td '+holidayBg+' '+onclick+'>'+(holiday?'<div style="font-size:9px;color:var(--warn-deep);margin-bottom:2px">\uD83C\uDFD6 +TOIL</div>':'')+'<span class="shift-pill sp-work">'+sh.start+'\u2013'+sh.finish+'</span>'+leg2Html+'</td>';
        }else{
          bodyHtml+='<td '+holidayBg+' '+onclick+'>'+(holiday?'<div style="font-size:9px;color:var(--warn-deep);margin-bottom:2px">\uD83C\uDFD6</div>':'')+'<button class="sp-empty">+</button></td>';
        }
      }else if(sh.type==='off'){
        bodyHtml+='<td '+holidayBg+' '+onclick+'><span class="shift-pill sp-off">D/O</span></td>';
      }else if(sh.type==='toil'){
        // Bug C (v80.X): non-work types no longer count toward "Rota" hours.
        // They live on a separate "Off" line so the headline number reflects
        // actual work scheduled. Bug reporter expected 32h for a staff member (4 work
        // days × 8h) with 3 days holiday — not 56h (work + holiday combined).
        nonWorkHours+=8;
        // v132.94: a part-balance TOIL Day shows its hours, so it is visible.
        bodyHtml+='<td '+holidayBg+' '+onclick+'><span class="shift-pill sp-toil">TOIL'+((sh.toilHours>0&&sh.toilHours<8)?' '+sh.toilHours+'h':'')+'</span></td>';
      }else if(sh.type==='holiday'){
        nonWorkHours+=8;
        bodyHtml+='<td '+holidayBg+' '+onclick+'><span class="shift-pill sp-holiday">\u2600\uFE0F Hol</span></td>';
      }else if(sh.type==='sick'){
        nonWorkHours+=8;
        bodyHtml+='<td '+holidayBg+' '+onclick+'><span class="shift-pill sp-sick">\uD83E\uDD12 Sick</span></td>';
      }else if(sh.type==='apl'){
        // v129.15 Authorised Paid Leave \u2014 paid, balance-neutral; reason on the pill title
        nonWorkHours+=8;
        // v129.18: escaped (and String()-coerced — a non-string leave_reason
        // used to throw on .charAt and blank the whole week grid). The value is
        // now also validated server-side in shift-guard; this is the second layer.
        var aplReasonTxt=sh.leaveReason?String(sh.leaveReason):'';
        var aplTitle=aplReasonTxt?(' title="'+escapeHtml(aplReasonTxt.charAt(0).toUpperCase()+aplReasonTxt.slice(1)+' leave')+'"'):'';
        bodyHtml+='<td '+holidayBg+' '+onclick+'><span class="shift-pill sp-apl"'+aplTitle+'>APL</span></td>';
      }else if(sh.type==='oos'){
        var oosLabel=sh.oosStore?sh.oosStore.replace('ELC - ','').split(' - ').pop().split(' ').slice(0,2).join(' '):'Not in store';
        var oosArrow=sh.oosStore?'\u2197 ':'\u2715 ';
        bodyHtml+='<td style="background:var(--info-bg);cursor:pointer" '+onclick+'><span class="shift-pill sp-oos">'+oosArrow+oosLabel+'</span></td>';
      }
    });
    // Hours tracker cell
    var contracted=getContractedHours(s.id);
    // Bug C: compare against (contracted minus paid time off this week).
    // 56h contract with 3 holiday days = 32h expected work — fully scheduled
    // when totalHours === 32, not under by 24h.
    var expectedWork=Math.max(0, contracted - nonWorkHours);
    var isPast=currentWeekStart<getWeekStart(new Date());   // v136.8: the store's own week (Qatar starts Sunday)
    var actualHours=0;
    if(isPast){
      var weekSummary=Clock.getForWeek(s.id,currentWeekStart);
      if(weekSummary&&weekSummary.totalH){actualHours=weekSummary.totalH;}
    }
    var rotaColor=totalHours>expectedWork?'over':totalHours>=expectedWork-1?'ok':totalHours>0?'warn':'muted';
    var actualColor=actualHours>contracted?'over':actualHours>=contracted-1?'ok':actualHours>0?'warn':'muted';
    var trackerHtml='<div class="hours-tracker-cell">';
    trackerHtml+='<div class="ht-row"><span class="ht-label">Contract</span><span class="ht-val muted">'+contracted+'h</span></div>';
    trackerHtml+='<div class="ht-row"><span class="ht-label">Rota</span><span class="ht-val '+rotaColor+'">'+(totalHours>0?totalHours.toFixed(1)+'h':'\u2014')+'</span></div>';
    // Bug C: show paid time off as a separate line so context isn't lost.
    // Hidden when zero so most weeks (no holiday/sick/TOIL) stay compact.
    if(nonWorkHours>0){
      trackerHtml+='<div class="ht-row" title="Holiday + Sick + TOIL hours this week"><span class="ht-label" style="opacity:.7">Off</span><span class="ht-val muted">+'+nonWorkHours+'h</span></div>';
    }
    if(isPast){trackerHtml+='<div class="ht-row"><span class="ht-label">Actual</span><span class="ht-val '+actualColor+'">'+(actualHours>0?actualHours.toFixed(1)+'h':'\u2014')+'</span></div>';}
    trackerHtml+='</div>';
    bodyHtml+='<td>'+trackerHtml+'</td></tr>';
  }

  // Render home staff first, then cross-store (cover) staff
  homeStaff.forEach(function(s){ renderStaffRow(s, false); });
  crossStoreStaff.forEach(function(s){ renderStaffRow(s, true); });

  // Heads row
  bodyHtml+='<tr class="heads-row"><td>Heads</td>';
  displayDays.forEach(function(d){bodyHtml+='<td>'+(headsPerDay[d]||'\u2014')+'</td>';});
  bodyHtml+='<td></td></tr>';
  // Delivery row
  bodyHtml+='<tr style="background:var(--rota-danger-soft)"><td style="font-size:10px;font-weight:700;color:var(--danger-deep)">\uD83D\uDE9A Delivery</td>';
  displayDays.forEach(function(d,i){
    var dayDelivs=deliveries[d]||[];
    if(dayDelivs.length){
      bodyHtml+='<td onclick="requireManager(\'setDelivery\',\''+d+'\')" style="cursor:pointer">'+dayDelivs.map(function(x){return'<div class="delivery-pill">'+x.time+'</div>';}).join('')+'</td>';
    }else{
      bodyHtml+='<td onclick="requireManager(\'setDelivery\',\''+d+'\')" style="cursor:pointer"><button class="sp-empty" style="border-color:var(--rota-danger-mid);color:var(--rota-danger-mid)">+</button></td>';
    }
  });
  bodyHtml+='<td></td></tr>';
  // v48: cross-store staff appear in the main table above. This button opens a modal
  // to add cover from another store — it writes proper DB shifts with work_store=currentStore.
  bodyHtml+='<tr><td colspan="9" style="padding:5px 0"><div onclick="requireManager(\'openCoverAdd\',null)" style="display:inline-block;padding:7px 16px;border:2px dashed var(--rota-warn-line);border-radius:10px;cursor:pointer;text-align:center;color:var(--warn-deep);font-size:11px;font-weight:600;background:var(--rota-warn-soft);-webkit-tap-highlight-color:transparent">+ Add cover from another store</div></td></tr>';
  document.getElementById('rotaTbody').innerHTML=bodyHtml;
}
render.renderRotaWeek = renderRotaWeek;

function renderDayView(){
  var nightMode=document.getElementById('nightToggle').checked;
  var HSTART=HOUR_STD_START;
  var HEND=nightMode?HOUR_NIGHT_END:HOUR_STD_END;
  var totalMins=(HEND-HSTART)*60;
  var pills=document.getElementById('dayPills');pills.innerHTML='';
  // v136.8: walk the days in the store's own week order. A Qatar week starts on
  // Sunday, so DAYS (Mon first) put Sunday's date on the Mon button and the
  // public-holiday flag on the wrong day. The week view already did this.
  var displayDays=getDisplayDays();
  displayDays.forEach(function(day,i){
    var dateStr=weekDateStr(currentWeekStart,i);
    var holiday=getHolidayForDate(currentStore,dateStr);
    var d=new Date(currentWeekStart);d.setDate(d.getDate()+i);
    var label=day+' '+d.getDate();
    var btn=document.createElement('button');
    btn.className='day-pill'+(day===selectedDay?' active':'')+(holiday?' has-holiday':'');
    btn.textContent=label+(holiday?' \uD83C\uDFD6':'');
    btn.onclick=(function(dy){return function(){selectedDay=dy;renderDayView();};})(day);
    pills.appendChild(btn);
  });
  var dayIdx=displayDays.indexOf(selectedDay);
  var targets=getTargets(currentStore,currentWeekStart);
  var target=targets[selectedDay]||0;
  document.getElementById('dtbDay').textContent=DAY_FULL[selectedDay]+' Target';
  document.getElementById('dtbVal').textContent=(target||0).toLocaleString();
  document.getElementById('dtbFill').style.width=target?Math.min(100,Math.round(target/Math.max.apply(null,Object.values(targets))*100))+'%':'0%';
  var hours=[];for(var h=HSTART;h<HEND;h++)hours.push(h%24);
  var dateStr=weekDateStr(currentWeekStart,dayIdx);
  var holiday=getHolidayForDate(currentStore,dateStr);
  var staff=getStoreStaff(currentStore).slice();
  // v123.25: also show anyone COVERING at this store on the selected day — their
  // shift today has workStore === currentStore but their home store is elsewhere.
  // They were missing from the Day View: a cover only appeared in their HOME store
  // (as "Not in store"), never at the store they were actually at. Home staff
  // covering elsewhere still render as the "Not in store" bar below, which managers
  // want for planning — so we ADD covers here, we do not remove anyone. (The weekly
  // view already does this; the day view had diverged.)
  (function(){
    var homeIds = {};
    staff.forEach(function(s){ homeIds[s.id] = true; });
    var wkS = localDateStr(currentWeekStart);
    Object.keys(shifts).forEach(function(key){
      if (key.indexOf(wkS + '_') !== 0) return;
      if (!key.endsWith('_' + selectedDay)) return;
      var sh = shifts[key];
      if (!sh || sh.workStore !== currentStore) return;
      var parts = key.split('_');
      var sid = parseInt(parts[3] || parts[parts.length - 2]);
      if (isNaN(sid) || homeIds[sid]) return;
      var cs = ALL_STAFF.find(function(x){ return x.id === sid; });
      if (cs) { staff.push(cs); homeIds[sid] = true; }
    });
  })();
  var lunchWindows=[];
  var isWeekendDay=(selectedDay==='Fri'||selectedDay==='Sat');
  var tOpen=9;var tClose=isWeekendDay?24:22;
  var html='<div class="tl-header"><div class="tl-name-col">Staff</div><div class="tl-hours">';
  hours.forEach(function(h,i){
    var next=hours[i+1]!==undefined?hours[i+1]:(h+1)%24;
    var isClosed=h<tOpen||h>=tClose;
    html+='<div class="tl-hr'+(isClosed?' closed':'')+'">'+pad(h)+'-'+pad(next)+'</div>';
  });
  html+='</div></div>';
  staff.forEach(function(s){
    var sk=shiftKey(s.id,selectedDay);
    var sh=getShift(s.id,selectedDay,currentWeekStart);
    var ev=Clock.events[s.id]||{};
    html+='<div class="tl-row"><div class="tl-name" translate="no" title="'+escapeHtml(s.name)+'">'+escapeHtml(s.name.split(' ')[0])+'</div>';   // v132.85
    html+='<div class="tl-track" style="position:relative">';
    // v136.8: the closed-hours shade was drawn twice (nearly solid) in a fixed
    // light grey that showed as bright bands in Dark. Once, from a theme token.
    if(tOpen>HSTART){html+='<div style="position:absolute;top:0;bottom:0;left:0;width:'+pct((tOpen-HSTART)*60,totalMins)+';background:var(--rota-closed,rgba(243,244,246,0.85));z-index:1;pointer-events:none"></div>';}
    if(tClose<HEND){html+='<div style="position:absolute;top:0;bottom:0;left:'+pct((tClose-HSTART)*60,totalMins)+';right:0;background:var(--rota-closed,rgba(243,244,246,0.85));z-index:1;pointer-events:none"></div>';}
    if(holiday){
      html+='<div class="tl-bar holiday" style="left:0;width:100%">\uD83C\uDFD6 '+holiday.name+'</div>';
    }else if(!sh||sh.type==='work'){
      if(sh&&sh.start){
        var sm=timeMins(sh.start)-HSTART*60;
        var fm=timeMins(sh.finish)-HSTART*60;
        var lunchM=sh.lunch||60;
        var lunchAtM=sh.lunchAt?timeMins(sh.lunchAt)-HSTART*60:sm+(fm-sm)/2-lunchM/2;
        var ls=lunchAtM,le=lunchAtM+lunchM;
        lunchWindows.push({name:s.name.split(' ')[0],start:ls,end:le});
        html+='<div class="tl-bar work" style="left:'+pct(sm,totalMins)+';width:'+pct(ls-sm,totalMins)+'">'+sh.start+'</div>';
        html+='<div class="tl-bar lunch" style="left:'+pct(ls,totalMins)+';width:'+pct(lunchM,totalMins)+';cursor:pointer" onclick="requireManager(\'editShift\','+s.id+',\''+selectedDay+'\')">L</div>';
        html+='<div class="tl-bar work" style="left:'+pct(le,totalMins)+';width:'+pct(fm-le,totalMins)+'">'+sh.finish+'</div>';
        if(ev.clockIn){
          var aim=timeMins(ev.clockIn)-HSTART*60;
          var aom=ev.clockOut?timeMins(ev.clockOut)-HSTART*60:(new Date().getHours()*60+new Date().getMinutes())-HSTART*60;
          html+='<div class="tl-actual" style="left:'+pct(Math.max(0,aim),totalMins)+';width:'+pct(Math.max(0,aom-aim),totalMins)+'"></div>';
        }
      }else{html+='<div class="tl-bar dayoff" style="left:0;width:100%;background:var(--border);color:var(--sub)">Not set</div>';}
    }else if(sh.type==='off'){html+='<div class="tl-bar dayoff" style="left:0;width:100%">Day Off</div>';}
    else if(sh.type==='toil'){html+='<div class="tl-bar toil" style="left:0;width:100%">TOIL Day'+((sh.toilHours>0&&sh.toilHours<8)?' ('+sh.toilHours+'h)':'')+'</div>';}   // v132.94
    else if(sh.type==='oos'){var oosLabel=sh.oosStore?'\u2197 '+sh.oosStore.replace('ELC - ','').split(' - ').pop():'\u2715 Not in store';html+='<div class="tl-bar" style="left:0;width:100%;background:#3b82f6">'+oosLabel+'</div>';}
    // Delivery bars
    (deliveries[selectedDay]||[]).forEach(function(d){
      var pts=d.time.split('-');if(!pts[0]||!pts[1])return;
      var dm=timeMins(pts[0].trim())-HSTART*60;var dem=timeMins(pts[1].trim())-HSTART*60;
      html+='<div class="delivery-bar" style="left:'+pct(Math.max(0,dm),totalMins)+';width:'+pct(Math.max(0,dem-dm),totalMins)+'"><div class="delivery-label">\uD83D\uDE9A</div></div>';
    });
    html+='</div></div>';
  });
  // Heads row
  html+='<div class="heads-tl-row"><div class="heads-tl-name">Heads</div><div class="heads-tl-vals">';
  hours.forEach(function(h){
    var count=staff.filter(function(s){
      var sh=getShift(s.id,selectedDay,currentWeekStart);
      if(!sh||!sh.start||sh.type==='oos')return false;
      if(sh.type!=='work')return false;
      var sm=timeMins(sh.start),fm=timeMins(sh.finish);
      return h*60>=sm&&h*60<fm;
    }).length;
    var c=count===0?'var(--red)':count>=3?'var(--green)':'var(--amber)';
    html+='<div class="heads-tl-val" style="color:'+c+'">'+(count||'')+'</div>';
  });
  html+='</div></div>';
  document.getElementById('timeline').innerHTML=html;
  var overlaps=[];
  for(var i=0;i<lunchWindows.length;i++)for(var j=i+1;j<lunchWindows.length;j++)if(lunchWindows[i].start<lunchWindows[j].end&&lunchWindows[j].start<lunchWindows[i].end)overlaps.push(lunchWindows[i].name+' & '+lunchWindows[j].name);
  var warn=document.getElementById('lunchWarning');
  if(overlaps.length){warn.style.display='block';document.getElementById('lunchWarnText').textContent='Lunch overlap: '+overlaps.join(', ');}
  else warn.style.display='none';
  var delivSummEl=document.getElementById('dayDelivSummary');
  if(!delivSummEl){delivSummEl=document.createElement('div');delivSummEl.id='dayDelivSummary';delivSummEl.style.marginTop='6px';document.getElementById('lunchWarning').parentNode.appendChild(delivSummEl);}
  var dayDelivs=deliveries[selectedDay]||[];
  delivSummEl.innerHTML='';
  if(dayDelivs.length){
    dayDelivs.forEach(function(d){var sp=document.createElement('span');sp.className='delivery-pill';sp.setAttribute('translate','no');sp.style.marginInlineEnd='4px';sp.textContent='\uD83D\uDE9A '+d.time+(d.note?' \u2014 '+d.note:'');delivSummEl.appendChild(sp);});
    var editBtn=document.createElement('button');editBtn.className='set-delivery-btn';editBtn.style.marginInlineStart='6px';editBtn.textContent='+ Edit';
    (function(day){editBtn.addEventListener('click',function(){requireManager('setDelivery',day);});})(selectedDay);delivSummEl.appendChild(editBtn);
  }else{
    var addBtn=document.createElement('button');addBtn.className='set-delivery-btn';addBtn.textContent='\uD83D\uDE9A Add delivery window';
    (function(day){addBtn.addEventListener('click',function(){requireManager('setDelivery',day);});})(selectedDay);delivSummEl.appendChild(addBtn);
  }
}
render.renderDayView = renderDayView;

var _vsActualTried={};
function renderVsActual(){
  var staff=getStoreStaff(currentStore);
  var displayDays=getDisplayDays();
  var isPast=currentWeekStart<getWeekStart(new Date());
  if(!isPast){document.getElementById('vsActualList').innerHTML='<div style="text-align:center;padding:30px;color:var(--sub)"><div style="font-size:36px;margin-bottom:8px">\uD83D\uDCC5</div>Select a past week to compare scheduled vs actual times.</div>';return;}
  // v136.8: load each day's clock record for this past week once (in parallel),
  // then draw again. Days not loaded yet show a dash until then.
  var wkMissing=displayDays.map(function(d,i){return weekDateStr(currentWeekStart,i);})
    .filter(function(ds){return !(Clock.history&&Clock.history[ds]);});
  var wkKey=currentStore+'|'+localDateStr(currentWeekStart);
  if(wkMissing.length&&!_vsActualTried[wkKey]&&typeof Clock.loadFromDB==='function'){
    _vsActualTried[wkKey]=true;   // one try per week per page load: a failed read must not loop
    Promise.all(wkMissing.map(function(ds){return Clock.loadFromDB(ds);})).then(function(){
      var pane=document.getElementById('vsActualView');
      if(pane&&pane.style.display!=='none'&&wkKey===currentStore+'|'+localDateStr(currentWeekStart)) renderVsActual();
    },function(){});
  }
  var html='';
  staff.forEach(function(s){
    var color=s.color||COLORS[s.id%COLORS.length];
    var daysHtml='';
    displayDays.forEach(function(day,i){
      var sk=shiftKey(s.id,day);var sh=getShift(s.id,day,currentWeekStart);
      var dateStr=weekDateStr(currentWeekStart,i);
      var holiday=getHolidayForDate(currentStore,dateStr);
      // v136.8: that day's own clock record. This used to fall back to TODAY's
      // live clock (Clock.events), so every past day showed today's times or
      // ABSENT. A date not loaded yet shows a dash, never ABSENT.
      var ev=Clock.getEventForDate(s.id,dateStr)||{};
      if(holiday){daysHtml+='<div class="hist-day holiday"><div class="hist-day-label">'+day+'</div><div class="hist-actual off">\uD83C\uDFD6</div></div>';return;}
      if(!sh||sh.type==='off'){daysHtml+='<div class="hist-day off"><div class="hist-day-label">'+day+'</div><div class="hist-actual off">D/O</div></div>';return;}
      if(sh.type==='toil'){daysHtml+='<div class="hist-day off"><div class="hist-day-label">'+day+'</div><div class="hist-actual off">TOIL</div></div>';return;}
      if(sh.type==='holiday'){daysHtml+='<div class="hist-day" style="background:var(--rota-warn-soft)"><div class="hist-day-label">'+day+'</div><div class="hist-actual" style="color:var(--warn-deep)">\u2600\uFE0F Hol</div></div>';return;}
      if(sh.type==='sick'){daysHtml+='<div class="hist-day" style="background:var(--rota-danger-soft)"><div class="hist-day-label">'+day+'</div><div class="hist-actual" style="color:var(--rota-rose-deep)">\uD83E\uDD12 Sick</div></div>';return;}
      if(sh.type==='oos'){daysHtml+='<div class="hist-day off"><div class="hist-day-label">'+day+'</div><div class="hist-actual" style="color:var(--info-deep)">\u2197</div></div>';return;}
      // v129.18: without this, an APL day (no clock event, no scheduled start)
      // fell through to the ABSENT branch below \u2014 maternity/compassionate leave
      // showing as unauthorised absence in the attendance history.
      if(sh.type==='apl'){daysHtml+='<div class="hist-day" style="background:var(--rota-teal-bg)"><div class="hist-day-label">'+day+'</div><div class="hist-actual" style="color:var(--rota-teal-deep)">\ud83d\udd4a\ufe0f APL</div></div>';return;}
      if(!(Clock.history&&Clock.history[dateStr])){daysHtml+='<div class="hist-day"><div class="hist-day-label">'+day+'</div><div class="hist-sched">'+(sh&&sh.start||'')+'</div><div class="hist-actual off">\u2026</div></div>';return;}
      if(!ev.clockIn){daysHtml+='<div class="hist-day missed"><div class="hist-day-label">'+day+'</div><div class="hist-sched">'+(sh.start||'?')+'</div><div class="hist-actual absent">ABSENT</div></div>';return;}
      var inDiff=timeMins(ev.clockIn)-timeMins(sh.start||'09:00');
      var cls=Math.abs(inDiff)<=5?'ok':inDiff>5?'late':'ok';
      daysHtml+='<div class="hist-day worked"><div class="hist-day-label">'+day+'</div><div class="hist-sched">'+sh.start+'\u2013'+sh.finish+'</div><div class="hist-actual '+cls+'">'+ev.clockIn+'\u2013'+(ev.clockOut||'?')+'</div><span class="vp '+(inDiff>5?'vp-neg':'vp-zero')+'">'+(inDiff>5?'+'+inDiff+'m':'On time')+'</span></div>';
    });
    html+='<div class="hist-card"><div class="hist-header"><div class="sn-dot" style="background:'+color+'"></div><div style="font-size:13px;font-weight:700" translate="no">'+escapeHtml(s.name)+'</div></div><div class="hist-days">'+daysHtml+'</div></div>';
  });
  document.getElementById('vsActualList').innerHTML=html||'<div style="padding:20px;color:var(--sub);text-align:center">No staff found</div>';
}
render.renderVsActual = renderVsActual;
